import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  cacheSpot,
  cacheSpots,
  getCachedSpot,
  getAllCachedSpots,
  getRecentlyViewedSpots,
  getCacheStats,
  clearOldCache,
  clearAllCache,
  isOffline,
  onConnectionChange,
  queuePost,
  getPendingPosts,
  getPendingPostsCount,
  removePendingPost,
  updatePendingPostStatus,
} from '@/lib/offlineCache';
import { useNostrPublish } from './useNostrPublish';
import { useToast } from './useToast';
import type { NostrEvent } from '@nostrify/nostrify';
import type { SkateSpot } from '@/lib/types';

// ============ Connection Status Hook ============

/**
 * Hook to track online/offline status
 */
export function useOnlineStatus() {
  const [online, setOnline] = useState(!isOffline());

  useEffect(() => {
    const unsubscribe = onConnectionChange(setOnline);
    return unsubscribe;
  }, []);

  return online;
}

// ============ Spot Caching Hooks ============

/**
 * Hook to cache a spot when viewing
 */
export function useCacheSpotOnView() {
  return useCallback(async (event: NostrEvent, spot: SkateSpot) => {
    try {
      await cacheSpot(event, spot);
    } catch (error) {
      console.warn('[OfflineCache] Failed to cache spot:', error);
    }
  }, []);
}

/**
 * Hook to cache multiple spots (e.g., when loading map)
 */
export function useCacheSpotsOnLoad() {
  return useCallback(async (spotsWithEvents: Array<{ event: NostrEvent; spot: SkateSpot }>) => {
    try {
      await cacheSpots(spotsWithEvents);
    } catch (error) {
      console.warn('[OfflineCache] Failed to cache spots:', error);
    }
  }, []);
}

/**
 * Hook to get a cached spot (for offline fallback)
 */
export function useCachedSpot(pubkey: string | undefined, identifier: string | undefined) {
  return useQuery({
    queryKey: ['cached-spot', pubkey, identifier],
    queryFn: async () => {
      if (!pubkey || !identifier) return null;
      return getCachedSpot(pubkey, identifier);
    },
    enabled: !!pubkey && !!identifier,
    staleTime: Infinity, // Cache never goes stale
  });
}

/**
 * Hook to get all cached spots (for offline browsing)
 */
export function useCachedSpots() {
  return useQuery({
    queryKey: ['cached-spots'],
    queryFn: getAllCachedSpots,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Hook to get recently viewed spots
 */
export function useRecentlyViewedSpots(limit: number = 50) {
  return useQuery({
    queryKey: ['recently-viewed-spots', limit],
    queryFn: () => getRecentlyViewedSpots(limit),
    staleTime: 1000 * 60,
  });
}

/**
 * Hook to get cache statistics
 */
export function useCacheStats() {
  return useQuery({
    queryKey: ['cache-stats'],
    queryFn: getCacheStats,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to manage cache (clear old/all)
 */
export function useCacheManagement() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const clearOld = useMutation({
    mutationFn: async (maxAgeDays: number = 7) => {
      const maxAgeMs = maxAgeDays * 24 * 60 * 60 * 1000;
      return clearOldCache(maxAgeMs);
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['cached-spots'] });
      queryClient.invalidateQueries({ queryKey: ['cache-stats'] });
      toast({
        title: 'Cache cleaned',
        description: `Removed ${count} old spots from cache`,
      });
    },
  });

  const clearAll = useMutation({
    mutationFn: clearAllCache,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cached-spots'] });
      queryClient.invalidateQueries({ queryKey: ['cache-stats'] });
      toast({
        title: 'Cache cleared',
        description: 'All cached spots have been removed',
      });
    },
  });

  return { clearOld, clearAll };
}

// ============ Pending Posts Hooks ============

/**
 * Hook to get pending posts count
 */
export function usePendingPostsCount() {
  const isOnline = useOnlineStatus();

  return useQuery({
    queryKey: ['pending-posts-count'],
    queryFn: getPendingPostsCount,
    refetchInterval: isOnline ? 5000 : false, // Only refresh when online
  });
}

/**
 * Hook to sync pending posts when coming back online
 */
export function useSyncPendingPosts() {
  const isOnline = useOnlineStatus();
  const queryClient = useQueryClient();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const { toast } = useToast();
  const [isSyncing, setIsSyncing] = useState(false);

  // Attempt to sync when coming back online
  useEffect(() => {
    if (!isOnline || isSyncing) return;

    const syncPosts = async () => {
      const pending = await getPendingPosts();
      if (pending.length === 0) return;

      setIsSyncing(true);
      let successCount = 0;
      let failCount = 0;

      for (const post of pending) {
        try {
          await updatePendingPostStatus(post.id, 'publishing');
          await publishEvent({
            kind: post.kind,
            content: post.content,
            tags: post.tags,
          });
          await removePendingPost(post.id);
          successCount++;
        } catch (error) {
          console.error('[OfflineSync] Failed to publish post:', error);
          await updatePendingPostStatus(post.id, 'failed', post.retries + 1);
          failCount++;
        }
      }

      setIsSyncing(false);
      queryClient.invalidateQueries({ queryKey: ['pending-posts-count'] });

      if (successCount > 0 || failCount > 0) {
        toast({
          title: 'Syncing complete',
          description: successCount > 0
            ? `Published ${successCount} queued post${successCount > 1 ? 's' : ''}`
            : `Failed to publish ${failCount} post${failCount > 1 ? 's' : ''}`,
          variant: failCount > 0 && successCount === 0 ? 'destructive' : 'default',
        });
      }
    };

    // Small delay before syncing to let network stabilize
    const timeout = setTimeout(syncPosts, 2000);
    return () => clearTimeout(timeout);
  }, [isOnline, isSyncing, publishEvent, queryClient, toast]);

  return { isSyncing };
}

/**
 * Hook to queue a post for offline publishing
 */
export function useQueuePost() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ kind, content, tags }: { kind: number; content: string; tags: string[][] }) => {
      return queuePost(kind, content, tags);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-posts-count'] });
      toast({
        title: 'Post queued',
        description: 'Your post will be published when you\'re back online',
      });
    },
  });
}
